import React, { useMemo } from 'react';
import GenericTable from '../general/generic-table';
import { ObjectiveExpandedRow } from './objective-expanded-row';
import { utilService } from "../../services/util-service"
import { newHttpService } from "../../services/new-http-service"
import { updateObjectiveMutation } from "../util-cmps/graphql-mutations/update-objective"
import { getRequiredSystems, SystemTypes } from "../forms/is-required-system"

interface ObjectiveTableProps {
  data: any;
  onDataChange?: () => void;
  onEditClick?: (objective: any) => void;
}

const createMapping = (title: string, location: string[], type: string, trueText?: string, falseText?: string) => ({
  title, location, type, trueText, falseText
})

const SYSTEM_LABELS: Record<string, string> = {
  [SystemTypes.IN_SCOPE]: 'Main',
  [SystemTypes.HR]: 'HR',
  [SystemTypes.TICKET]: 'Ticket',
}

const formatRequiredSystems = (objectiveNumber: number | null): string => {
  const systems = getRequiredSystems(objectiveNumber)
  return systems.map(s => SYSTEM_LABELS[s] || s).join(', ')
}

const mapping = [
  createMapping('ID', ['id'], 'int'),
  createMapping('Objective Name', ['name'], 'string'),
  createMapping('Objective Number', ['objectiveNumber'], 'string'),
  createMapping('Required Systems', ['_requiredSystems'], 'string'),
  createMapping('Domain', ['domain'], 'string'),
  createMapping('Domain Abbreviation', ['domainAbbreviation'], 'string'),
  createMapping('Abbreviation', ['abbreviation'], 'string'),
  createMapping('Frequency', ['frequency'], 'string'),
  createMapping('Type', ['type'], 'string'),
  createMapping('IT Dependency', ['ITDependency'], 'string'),
  createMapping('Test Type', ['testType'], 'string'),
  createMapping('Specification', ['specification'], 'string'),
  createMapping('Population Type', ['populationType'], 'string'),
  createMapping('Key Objective', ['isKeyObjective'], 'boolean', 'Yes', 'No'),
]

const ObjectiveTable: React.FC<ObjectiveTableProps> = ({ data, onDataChange, onEditClick }) => {
  const processedData = useMemo(() => data.map((record: any) => ({
    ...record,
    _requiredSystems: formatRequiredSystems(record.objectiveNumber ?? null),
  })), [data])

  const tableData: any[][] = processedData.map((record: any) =>
    mapping.map((m) => ({
      title: m.title,
      value: utilService.getCellValue(record, m),
      type: m.type,
    }))
  )

  const updateObjective = async (objectiveId: number, fields: { isKeyObjective?: boolean }) => {
    const query = updateObjectiveMutation()
    const variables = { objectiveId, ...fields }
    await newHttpService.graphql('auditech', { query, variables })
    onDataChange?.()
  }

  const onCellClick = (rowData: any[], _rowIndex: number, cellTitle: string) => {
    const idCell = rowData.find((cell: any) => cell.title === 'ID')
    const objectiveId = Number(idCell?.value)
    if (!objectiveId) return

    const rawItem = data.find((item: any) => Number(item.id) === objectiveId)
    if (!rawItem) return

    if (cellTitle === 'Key Objective') {
      const newValue = !rawItem.isKeyObjective
      const action = newValue ? 'mark as key objective' : 'unmark as key objective'
      if (window.confirm(`Are you sure you want to ${action} objective ${objectiveId}?`)) {
        updateObjective(objectiveId, { isKeyObjective: newValue })
      }
    }
  }

  const renderExpandedRow = (rowData: any[]) => {
    const idCell = rowData.find((cell: any) => cell.title === 'ID')
    const objectiveId = Number(idCell?.value)
    const rawItem = data.find((item: any) => Number(item.id) === objectiveId)
    if (!rawItem) return null
    return <ObjectiveExpandedRow objective={rawItem} onEditClick={onEditClick || (() => {})} />
  }

  return (
    <GenericTable
      data={tableData}
      recordName="Objectives"
      renderExpandedRow={renderExpandedRow}
      onCellClick={onCellClick}
      clickableCells={['Key Objective']}
      hiddenByDefault={['ID', 'Domain Abbreviation', 'Key Objective']}
      filterableColumns={['Objective Name', 'Domain', 'Domain Abbreviation', 'Frequency', 'Type', 'IT Dependency', 'Test Type', 'Specification', 'Population Type', 'Key Objective']}
    />
  );
};

export default ObjectiveTable;
